import { IBuildThankYouRedirectUrlOptions } from 'typings';

/**
 * @param clientOrigin The origin of the client's website. This should be passed through the query string, to the widget, automatically.
 * @param clientQuery The query string of the client's website. This should also be passed through the query string, to the widget, automatically.
 * @param thankYouRedirectUrl The `thankYouRedirectUrl` theme setting
 */
export const buildThankYouRedirectUrl = (
  clientUrlString: string,
  thankYouRedirectUrl: string,
  options?: IBuildThankYouRedirectUrlOptions,
): string | null => {
  // If `thankYouRedirectUrl` is a relative path, and we don't have a clientUrl,
  // there's no way for us to create the URL, since all we have is a path. In
  // this case, we'll return null.
  if (thankYouRedirectUrl[0] === '/' && !clientUrlString) {
    return null;
  }

  if (!clientUrlString) {
    // In situations where `clientUrl` is an empty string (e.g., in app mode)
    // assume, given the check above, that `thankYouRedirectUrl` is an absolute
    // URL. Instead of parsing the origin out of it, we can use our window's
    // origin, and since `thankYouRedirectUrl` is absolute, the window's origin
    // will be discarded when creating `redirectUrl`
    clientUrlString = window.location.href;
  } else {
    clientUrlString = window.decodeURIComponent(clientUrlString);
  }

  const redirectUrl = new URL(thankYouRedirectUrl, clientUrlString);
  const clientUrl = new URL(clientUrlString);

  if (clientUrl.search) {
    // Add the client query string to our redirect url query string
    redirectUrl.search +=
      (redirectUrl.search ? '&' : '') + clientUrl.search.slice(1);
  }

  // Append Options to URL
  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      if (value) {
        redirectUrl.searchParams.set(toSnakeCase(key), value);
      }
    });
  }

  // Add origin URL without query string
  redirectUrl.searchParams.set(
    'widget_source_url',
    clientUrl.toString().split('?')[0] ?? '',
  );

  // Remove `se_action` from the redirect URL so the widget does not reopen
  redirectUrl.searchParams.delete('se_action');

  return redirectUrl.toString();
};

const toSnakeCase = (key: string): string => {
  let snakeCaseKey = key.replace(
    /([A-Z]+|[0-9]+)/g,
    (letter) => `_${letter.toLowerCase()}`,
  );

  // Add `se_` to some analytics fields
  // so they remain as they were initially passed in
  if (
    [
      'affiliate',
      'campaign',
      'custom_field_1',
      'custom_field_2',
      'custom_field_3',
      'custom_field_4',
      'custom_field_5',
      'custom_field_6',
      'custom_field_7',
      'custom_field_8',
      'custom_field_9',
      'custom_field_10',
      'lead_source',
      'subcontractor',
    ].includes(snakeCaseKey)
  ) {
    snakeCaseKey = 'se_' + snakeCaseKey;
  }

  return snakeCaseKey;
};
